import { defineStore } from 'pinia'

import EventBus, { GlobalEvents } from '@shared/event-bus'
import { ErrorTypes } from '@shared/models/enums/http'
import { StoreState } from '@shared/models/interfaces/store'
import { IFileUpload } from '@shared/models/proto/base/file-upload'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { isNetworkError } from '@shared/utils/helpers/error-guards/common-errors'
import { createError } from '@shared/utils/helpers/errors/error-builder'
import { isOnline } from '@shared/utils/helpers/general/is-online'
import {
  getInitialState,
  updateState,
} from '@shared/utils/store/http-request-state'

import { NonqliqMessage } from '@protos/chat/nonqliq_message_pb'

import {
  NonqliqMessageCreateDTO,
  NonqliqMessageIndexDTO,
} from '@pc/models/proto/dto/non-qliq-messages'
import {
  NonqliqMessageItemRO,
  NonqliqMessagesIndexRO,
} from '@pc/models/proto/ro/non-qliq-messages'

export interface State {
  messages: StoreState<NonqliqMessagesIndexRO>
  sendMessageState: StoreState<{
    data: NonqliqMessage
  }>
}

const state = (): State => ({
  messages: getInitialState(),
  sendMessageState: getInitialState(),
})

export const apiTransport = new ApiTransport(resourcePath, 'pc')

export const useNonqliqMessagesStore = defineStore('nonqliq_messages', {
  state,

  actions: {
    async fetchMessages(dto: { page: number; perPage: number }) {
      this.messages = updateState(this.messages)
      try {
        const response = await apiTransport.get<
          NonqliqMessageIndexDTO,
          NonqliqMessagesIndexRO
        >(
          '/nonqliq_messages',
          new NonqliqMessageIndexDTO(dto),
          NonqliqMessagesIndexRO
        )

        const { data: newMessages, meta } = response ?? { meta: {}, data: [] }
        const { data: existingMessages = [] } = this.messages.data ?? {}

        const dataToSet =
          dto.page === 1
            ? { data: newMessages, meta }
            : {
                meta,
                data: [...existingMessages, ...newMessages],
              }
        this.messages = updateState(
          this.messages,
          new NonqliqMessagesIndexRO(dataToSet)
        )
      } catch (error) {
        this.messages = updateState(this.messages, error as Error)
        throw error
      }
    },

    async sendMessage(message: { text: string }, files: IFileUpload[]) {
      const isOnlineUser = isOnline()
      this.sendMessageState = updateState(this.sendMessageState)

      try {
        if (!isOnlineUser) {
          const networkError = createError(ErrorTypes.NETWORK)
          throw networkError
        }

        const dto = {
          files,
          data: {
            ...message,
          },
        }

        const response = await apiTransport.post(
          '/nonqliq_messages',
          new NonqliqMessageCreateDTO(dto),
          NonqliqMessageCreateDTO,
          NonqliqMessageItemRO,
          {
            onUploadProgress({ total, loaded }) {
              EventBus.emit(GlobalEvents.AsyncRequestProgress, {
                total,
                loaded,
              })
            },
          }
        )

        const { data } = response ?? { data: [] }

        this.sendMessageState = updateState(this.sendMessageState, {
          data: data || new NonqliqMessage(),
        })
        return data
      } catch (error) {
        const serverError = createError(ErrorTypes.SERVER)
        const messageError = isNetworkError(error as Error)
          ? error
          : serverError

        this.sendMessageState = updateState(
          this.sendMessageState,
          messageError as Error
        )
        throw messageError
      }
    },

    addMessageToList(message?: NonqliqMessage) {
      if (message) {
        const { data: existingMessages = [], meta } = this.messages.data ?? {}

        this.messages = updateState(
          this.messages,
          new NonqliqMessagesIndexRO({
            meta,
            data: [message, ...existingMessages],
          })
        )
      }
    },

    resetSendMessageState() {
      this.sendMessageState = getInitialState()
    },
  },
})
